import type { WorkflowSubmissionSyncState } from './workflow-state.types';
import { inject, Injectable } from '@angular/core';
import {
  CreateWorkflowSuccessResponse,
  DeleteWorkflowResponse,
  GetWorkflowsListResponseSuccess,
  RequestWorkflow,
  RequestWorkflowWithNodes,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  SyncWorkflowFormErrorResponse,
  SyncWorkflowFormResponse,
  SyncWorkflowRemoveFileResponse,
  SyncWorkflowUploadFileResponse,
  Workflow,
  WorkflowExternal,
  WorkflowFormSubmission,
  WorkflowFormSubmissionExternal,
  WorkflowFormSubmissionFile,
  WorkflowFormSubmissionFileExternal,
  WorkflowInput,
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
  WorkflowSchema,
} from '@workflows/types';
import { AccountService } from '@core/services/account/account.service';
import { RequestService } from '@core/services/request/request.service';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  WorkflowApiService,
  WorkflowDataService,
  WorkflowSelectorService,
} from './';
import { RequestDTO } from '@core/types';

/**
 * Сервис для управления состоянием workflow и API операций
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowStateService {
  private readonly workflowApiService = inject(WorkflowApiService);
  private readonly accountService = inject(AccountService);
  private readonly dataService = inject(WorkflowDataService);
  private readonly selectorService = inject(WorkflowSelectorService);
  private readonly requestService = inject(RequestService);

  /**
   * Создать или обновить submission формы
   */
  public updateFormSubmission(
    workflowId: string,
    nodeID: number,
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    submissionData?: WorkflowFormSubmissionExternal['data'],
    isValid?: boolean,
    updateLastVisitedFormId = true,
    syncState?: WorkflowSubmissionSyncState,
  ): void {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return;

    // Submission ищется по экземпляру (requestWorkflowId + formId): форма одной версии
    // может принадлежать нескольким регламентам заявки
    const submissionIndex = wfData.submissions.findIndex(
      sb =>
        sb.requestWorkflowId === workflowId && sb.workflowNodeFormId === formId,
    );

    if (submissionIndex === -1) {
      const form = wfData.workflowNodesForms.find(f => f.id === formId);

      if (!form) return;

      const requestWorkflow = wfData.requestsWorkflows.find(
        rw =>
          rw.id === workflowId &&
          rw.nodes.some(node => node.id === form.workflowNodeId),
      );

      if (!requestWorkflow) return;

      const now = new Date().toISOString();
      const currentUser = this.accountService.$currentUser();
      const newSubmission: WorkflowFormSubmissionExternal = {
        workflowNodeFormId: formId,
        requestWorkflowId: requestWorkflow.id,
        isValid: isValid ?? false,
        ...(syncState && {
          pendingSync: syncState.pendingSync,
          clientValid: syncState.clientValid,
        }),
        data: submissionData || {},
        createdAt: now,
        updatedAt: now,
        createdId: currentUser?.id || 0,
        files: [],
      };

      wfData.submissions.push(newSubmission);
    } else {
      if (submissionData) {
        wfData.submissions[submissionIndex].data = {
          ...(wfData.submissions[submissionIndex].data || {}),
          ...submissionData,
        };
      }
      if (isValid !== undefined) {
        wfData.submissions[submissionIndex].isValid = isValid;
      }
      // Состояние офлайн-синхронизации: pendingSync снимается ответом сервера
      wfData.submissions[submissionIndex].pendingSync = syncState?.pendingSync;
      wfData.submissions[submissionIndex].clientValid = syncState?.clientValid;
    }

    if (updateLastVisitedFormId) {
      const nextFormInNode = this.selectorService.getNextFormInNode(
        nodeID,
        formId,
      );

      if (nextFormInNode) {
        wfData.requestWorkflowsContext.lastVisitedFormId = nextFormInNode;
      } else {
        const nextFormInWorkflow = this.selectorService.getNextFormInWorkflow(
          workflowId,
          nodeID,
          formId,
        );

        if (nextFormInWorkflow) {
          wfData.requestWorkflowsContext.lastVisitedFormId = nextFormInWorkflow;
        } else {
          wfData.requestWorkflowsContext.lastVisitedFormId = formId;
        }
      }
    }

    const requestNumber = this.requestService.$request().requestNumber;

    this.dataService.updateWorkflowData(requestNumber, { ...wfData });
  }

  /**
   * Submission формы конкретного экземпляра регламента заявки
   */
  public getSubmission(
    requestWorkflowId: string,
    formId: number,
  ): WorkflowFormSubmissionExternal | undefined {
    const wfData = this.dataService.$requestsWorkflowData();

    return wfData?.submissions.find(
      sb =>
        sb.requestWorkflowId === requestWorkflowId &&
        sb.workflowNodeFormId === formId,
    );
  }

  /**
   * Добавить загруженный файл в submission экземпляра формы (без перезагрузки данных регламентов)
   */
  public addSubmissionFile(
    requestWorkflowId: string,
    nodeId: number,
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    submissionFile: WorkflowFormSubmissionFileExternal,
  ): void {
    if (!this.getSubmission(requestWorkflowId, formId)) {
      this.updateFormSubmission(
        requestWorkflowId,
        nodeId,
        formId,
        undefined,
        undefined,
        false,
      );
    }

    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) {
      return;
    }

    const submissions = wfData.submissions.map(submission =>
      submission.requestWorkflowId === requestWorkflowId &&
      submission.workflowNodeFormId === formId &&
      !(submission.files ?? []).some(file => file.id === submissionFile.id)
        ? {
            ...submission,
            files: [...(submission.files ?? []), submissionFile],
          }
        : submission,
    );
    const requestNumber = this.requestService.$request().requestNumber;

    this.dataService.updateWorkflowData(requestNumber, {
      ...wfData,
      submissions,
    });
  }

  public getFilesFromSubmission(
    submissionId: WorkflowFormSubmission['id'],
  ): WorkflowFormSubmissionFileExternal[] {
    const wfData = this.dataService.$requestsWorkflowData();

    return (
      wfData?.submissions.find(({ id }) => id === submissionId)?.files || []
    );
  }

  public removeSubmissionFile(
    requestWorkflowId: string,
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    submissionFileId: WorkflowFormSubmissionFile['id'],
  ): void {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) {
      return;
    }

    const submissions = wfData.submissions.map(submission =>
      submission.requestWorkflowId === requestWorkflowId &&
      submission.workflowNodeFormId === formId &&
      Array.isArray(submission.files)
        ? {
            ...submission,
            files: submission.files.filter(
              file => file.id !== submissionFileId,
            ),
          }
        : submission,
    );
    const requestNumber = this.requestService.$request().requestNumber;

    this.dataService.updateWorkflowData(requestNumber, {
      ...wfData,
      submissions,
    });
  }

  // API Methods
  public syncWorkflow(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    formData: object,
    operationId?: string,
  ): Observable<SyncWorkflowFormResponse> {
    return this.workflowApiService
      .syncWorkflow(
        requestNumber,
        requestWorkflowId,
        nodeId,
        formId,
        formData,
        operationId,
      )
      .pipe(
        catchError(error => {
          if (error.status === 400) {
            const response = error.error as SyncWorkflowFormErrorResponse;
            const { failureReason } = response;

            if (
              failureReason ===
              SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors
            ) {
              return of(response);
            }
          }

          return throwError(() => error);
        }),
      );
  }

  public syncWorkflowUploadFile(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: string,
    formData: FormData,
    operationId?: string,
  ): Observable<SyncWorkflowUploadFileResponse> {
    return this.workflowApiService.syncWorkflowUploadFile(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      fieldId,
      formData,
      operationId,
    );
  }

  public syncWorkflowRemoveFile(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: WorkflowSchema,
    submissionFileId: WorkflowFormSubmissionFile['id'],
    operationId?: string,
  ): Observable<SyncWorkflowRemoveFileResponse> {
    return this.workflowApiService.syncWorkflowRemoveFile(
      requestNumber,
      workflowId,
      nodeId,
      formId,
      fieldId,
      submissionFileId,
      operationId,
    );
  }

  public completeRequestWorkflows(
    requestNumber: RequestDTO['requestNumber'],
    operationId?: string,
  ) {
    return this.workflowApiService.completeRequestWorkflows(
      requestNumber,
      operationId,
    );
  }

  // Debug Methods
  public getAllWorkflows(): Observable<Workflow[]> {
    return this.workflowApiService
      .getAllWorkflows()
      .pipe(map((response: GetWorkflowsListResponseSuccess) => response.data));
  }

  public removeWorkflow(
    workflowId: WorkflowExternal['id'],
  ): Observable<DeleteWorkflowResponse> {
    return this.workflowApiService.removeWorkflow(workflowId);
  }

  public getAllRequestWorkflowsOfRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<RequestWorkflow[]> {
    return this.workflowApiService.getAllRequestWorkflowsOfRequest(
      requestNumber,
    );
  }

  public attachSelectedWorkflows(
    requestNumber: RequestDTO['requestNumber'],
    selectedWorkflowIds: string[],
  ): Observable<{ message: string }> {
    return this.workflowApiService.attachSelectedWorkflows(
      requestNumber,
      selectedWorkflowIds,
    );
  }

  public detachAllWorkflows(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> {
    return this.workflowApiService.detachAllWorkflows(requestNumber);
  }

  public clearAllWorkflowsDataForRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> {
    return this.workflowApiService.clearAllWorkflowsDataForRequest(
      requestNumber,
    );
  }

  public importWorkflow(
    workflowJson: WorkflowInput,
    publish = false,
  ): Observable<CreateWorkflowSuccessResponse> {
    return this.workflowApiService.importWorkflow(workflowJson, publish);
  }
}
